import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";

import {
  addNodoCauchy,
  deleteNodoCauchy,
  getNodosCauchy,
  getOpcionesNodosCauchy,
  updateNodoCauchy,
  type PuntoCauchy,
  type TipoPuntoCauchy,
} from "@/lib/api";

/**
 * Configuración → Nodos Cauchy.
 *
 * Lista de nodos (PML) y zonas de carga (PND) para los que el script
 * docs/DART_Cauchy.R ajusta una Cauchy al DART (MDA − MTR) en ventana móvil y
 * guarda location/scale por día en etrm.dart_cauchy_diario. El script lee esta
 * lista (etrm.dart_cauchy_punto) en cada corrida y toma solo los activos.
 * Desactivar o quitar un punto no borra su historia.
 */
export function NodosCauchyPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin", "nodos-cauchy"], queryFn: getNodosCauchy });
  const refrescar = () => qc.invalidateQueries({ queryKey: ["admin", "nodos-cauchy"] });

  const cambiar = useMutation({
    mutationFn: ({ id, ...cambios }: {
      id: number; etiqueta?: string; activo?: boolean;
      var_lh_min?: number; var_lh_max?: number; var_tc?: number; var_confianza?: number;
    }) => updateNodoCauchy(id, cambios),
    onSuccess: refrescar,
  });
  const quitar = useMutation({
    mutationFn: (id: number) => deleteNodoCauchy(id),
    onSuccess: refrescar,
  });

  const puntos = q.data?.puntos ?? [];
  const activos = puntos.filter((p) => p.activo).length;
  const error = (cambiar.error ?? quitar.error) as Error | null;

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Nodos Cauchy</h1>
        <p className="text-sm text-slate-500 mt-1">
          Nodos y zonas de carga para los que se calcula diariamente el{" "}
          <span className="font-medium text-slate-700">location</span> y el{" "}
          <span className="font-medium text-slate-700">scale</span> de la distribución Cauchy del
          DART (PML MDA − PML MTR), con ventana móvil de {q.data?.ventana_dias ?? 14} días.
          Las zonas usan el precio zonal (PND) y los nodos el PML.
        </p>
        <p className="text-xs text-slate-400 mt-1">
          El cálculo toma solo los puntos activos en su siguiente corrida. Desactivar o quitar un
          punto no borra los días ya calculados.
        </p>
        <p className="text-xs text-slate-400 mt-1">
          <span className="font-medium text-slate-500">Parámetros VaR</span>: LH mín / LH máx del
          DART, tipo de cambio y confianza con que se corre el VaR DART de cada día de arbitraje
          cuyo centro de carga tenga asignado este punto (default −3,500 / 3,500 · 17.5 · 97 %).
          Los LH también truncan el ajuste de la Cauchy. Un cambio aplica{" "}
          <span className="font-medium text-slate-500">a partir de hoy</span>: los días de la serie
          y los VaR de días de operación anteriores conservan los valores con que se calcularon.
        </p>
      </div>

      {q.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {(q.error as Error)?.message}
        </div>
      )}

      <AgregarPunto onAgregado={refrescar} />

      {q.data && (
        <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-medium text-slate-800">Puntos configurados</div>
            <div className="text-xs text-slate-500">
              {activos} activo{activos === 1 ? "" : "s"} de {puntos.length}
            </div>
          </div>
          {error && (
            <div className="px-4 py-2 text-sm text-red-600 border-b border-slate-100">
              {error.message}
            </div>
          )}
          {puntos.length === 0 ? (
            <div className="p-4 text-sm text-slate-500">No hay puntos configurados.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="text-left font-medium px-4 py-2">Etiqueta</th>
                    <th className="text-left font-medium px-3 py-2">Tipo</th>
                    <th className="text-left font-medium px-3 py-2">Sistema · Clave</th>
                    <th className="text-left font-medium px-3 py-2">Serie diaria</th>
                    <th className="text-right font-medium px-3 py-2">Location</th>
                    <th className="text-right font-medium px-3 py-2">Scale</th>
                    <th className="text-left font-medium px-3 py-2">Parámetros VaR</th>
                    <th className="text-center font-medium px-3 py-2">Activo</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {puntos.map((p) => (
                    <FilaPunto
                      key={p.id}
                      p={p}
                      ocupado={cambiar.isPending || quitar.isPending}
                      onEtiqueta={(etiqueta) => cambiar.mutate({ id: p.id, etiqueta })}
                      onActivo={(activo) => cambiar.mutate({ id: p.id, activo })}
                      onVar={(v) => cambiar.mutate({ id: p.id, ...v })}
                      onQuitar={() => {
                        if (window.confirm(
                          `¿Quitar "${p.etiqueta}" de la lista? Los días ya calculados se conservan.`,
                        )) quitar.mutate(p.id);
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

type ParamsVar = { var_lh_min: number; var_lh_max: number; var_tc: number; var_confianza: number };

function FilaPunto({
  p, ocupado, onEtiqueta, onActivo, onVar, onQuitar,
}: {
  p: PuntoCauchy;
  ocupado: boolean;
  onEtiqueta: (v: string) => void;
  onActivo: (v: boolean) => void;
  onVar: (v: ParamsVar) => void;
  onQuitar: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(p.etiqueta);
  useEffect(() => setTexto(p.etiqueta), [p.etiqueta]);

  const guardar = () => {
    const t = texto.trim();
    if (t && t !== p.etiqueta) onEtiqueta(t);
    setEditando(false);
  };

  return (
    <tr className={p.activo ? "" : "bg-slate-50/60 text-slate-400"}>
      <td className="px-4 py-2">
        {editando ? (
          <span className="inline-flex items-center gap-1">
            <input
              autoFocus
              value={texto}
              maxLength={60}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") guardar();
                if (e.key === "Escape") { setTexto(p.etiqueta); setEditando(false); }
              }}
              className="rounded border border-slate-300 px-2 py-0.5 text-sm w-40"
            />
            <button type="button" title="Guardar" onClick={guardar}
              className="text-emerald-600 hover:text-emerald-800">
              <Check className="h-4 w-4" />
            </button>
            <button type="button" title="Cancelar"
              onClick={() => { setTexto(p.etiqueta); setEditando(false); }}
              className="text-slate-400 hover:text-slate-700">
              <X className="h-4 w-4" />
            </button>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 font-medium text-slate-800">
            <span className={p.activo ? "" : "text-slate-400"}>{p.etiqueta}</span>
            <button type="button" title="Cambiar etiqueta" onClick={() => setEditando(true)}
              className="text-slate-300 hover:text-slate-600">
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
          p.tipo === "zona" ? "bg-sky-50 text-sky-700" : "bg-violet-50 text-violet-700"
        }`}>
          {p.tipo === "zona" ? "Zona (PND)" : "Nodo (PML)"}
        </span>
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        {p.sistema} · {p.clave}
      </td>
      <td className="px-3 py-2 text-xs">
        {p.dias ? (
          <>
            {p.dias.toLocaleString("es-MX")} días
            <div className="text-slate-400">{fecha(p.desde)} – {fecha(p.hasta)}</div>
          </>
        ) : (
          <span className="text-slate-400">Pendiente de la siguiente corrida</span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{num(p.ultimo_location)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{num(p.ultimo_scale)}</td>
      <td className="px-3 py-2"><CeldaVar p={p} ocupado={ocupado} onGuardar={onVar} /></td>
      <td className="px-3 py-2 text-center">
        <button
          type="button"
          role="switch"
          aria-checked={p.activo}
          disabled={ocupado}
          title={p.activo ? "Desactivar" : "Activar"}
          onClick={() => onActivo(!p.activo)}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 ${
            p.activo ? "bg-emerald-500" : "bg-slate-300"
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            p.activo ? "translate-x-4" : "translate-x-0.5"
          }`} />
        </button>
      </td>
      <td className="px-3 py-2 text-right">
        <button type="button" title="Quitar de la lista" disabled={ocupado} onClick={onQuitar}
          className="text-slate-300 hover:text-red-600 disabled:opacity-50">
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

/** LH mín / LH máx / TC / confianza del VaR DART del arbitraje, editable en línea. */
function CeldaVar({ p, ocupado, onGuardar }: {
  p: PuntoCauchy; ocupado: boolean; onGuardar: (v: ParamsVar) => void;
}) {
  const inicial = () => ({
    lhMin: String(p.var_lh_min), lhMax: String(p.var_lh_max),
    tc: String(p.var_tc), conf: String(Math.round(p.var_confianza * 1000) / 10),
  });
  const [editando, setEditando] = useState(false);
  const [f, setF] = useState(inicial);
  useEffect(() => { if (!editando) setF(inicial()); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [p.var_lh_min, p.var_lh_max, p.var_tc, p.var_confianza, editando]);

  const lhMin = Number(f.lhMin), lhMax = Number(f.lhMax), tc = Number(f.tc), conf = Number(f.conf);
  const error = !Number.isFinite(lhMin) || !Number.isFinite(lhMax) || lhMin >= lhMax
    ? "LH mín debe ser menor que LH máx"
    : !(tc > 0) ? "TC mayor que cero"
    : !(conf > 0 && conf < 100) ? "Confianza entre 0 y 100 %"
    : null;

  if (!editando) {
    const hist = p.var_historial ?? [];
    const vigente = hist[0];
    const titulo = hist.map((v) =>
      `${v.vigente_desde <= "1900-01-01" ? "Desde siempre" : `Desde ${fecha(v.vigente_desde)}`}: `
      + `${num0(v.lh_min)} / ${num0(v.lh_max)} · TC ${v.tc} · ${Math.round(v.confianza * 1000) / 10} %`
      + (v.registrado_por ? ` (${v.registrado_por})` : "")).join("\n");
    return (
      <div className="text-xs tabular-nums text-slate-600 whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5">
          {num0(p.var_lh_min)} / {num0(p.var_lh_max)} · TC {p.var_tc.toLocaleString("es-MX", { maximumFractionDigits: 4 })} ·{" "}
          {Math.round(p.var_confianza * 1000) / 10} %
          <button type="button" title="Cambiar parámetros del VaR (aplica a partir de hoy)"
            onClick={() => setEditando(true)}
            className="text-slate-300 hover:text-slate-600">
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </span>
        {vigente && vigente.vigente_desde > "1900-01-01" && (
          <div className="text-slate-400 cursor-help" title={titulo}>
            vigente desde {fecha(vigente.vigente_desde)}
            {hist.length > 1 && ` · ${hist.length - 1} versión${hist.length > 2 ? "es" : ""} anterior${hist.length > 2 ? "es" : ""}`}
          </div>
        )}
      </div>
    );
  }

  const campo = (k: keyof typeof f, etiqueta: string, ancho = "w-20") => (
    <label className="text-[11px] text-slate-500">
      {etiqueta}
      <input value={f[k]} inputMode="decimal"
        onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))}
        className={`block ${ancho} rounded border border-slate-300 px-1.5 py-0.5 text-xs text-right tabular-nums text-slate-800`} />
    </label>
  );
  return (
    <div className="space-y-1">
      <div className="flex items-end gap-1.5">
        {campo("lhMin", "LH mín")}
        {campo("lhMax", "LH máx")}
        {campo("tc", "TC", "w-16")}
        {campo("conf", "Conf. %", "w-14")}
        <button type="button" title="Guardar" disabled={!!error || ocupado}
          onClick={() => {
            onGuardar({ var_lh_min: lhMin, var_lh_max: lhMax, var_tc: tc, var_confianza: conf / 100 });
            setEditando(false);
          }}
          className="pb-1 text-emerald-600 hover:text-emerald-800 disabled:opacity-40">
          <Check className="h-4 w-4" />
        </button>
        <button type="button" title="Cancelar" onClick={() => setEditando(false)}
          className="pb-1 text-slate-400 hover:text-slate-700">
          <X className="h-4 w-4" />
        </button>
      </div>
      {error && <div className="text-[11px] text-amber-700">{error}</div>}
    </div>
  );
}

function num0(v: number): string {
  return v.toLocaleString("es-MX", { maximumFractionDigits: 2 });
}

/** Alta: zona = lista completa del sistema; nodo = buscador en el catálogo NodosP. */
function AgregarPunto({ onAgregado }: { onAgregado: () => void }) {
  const [tipo, setTipo] = useState<TipoPuntoCauchy>("zona");
  const [sistema, setSistema] = useState("SIN");
  const [clave, setClave] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [etiqueta, setEtiqueta] = useState("");
  const [etiquetaTocada, setEtiquetaTocada] = useState(false);

  // El buscador de nodos espera a que se deje de teclear.
  const [termino, setTermino] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setTermino(busqueda.trim()), 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const opciones = useQuery({
    queryKey: ["admin", "nodos-cauchy", "opciones", tipo, sistema, tipo === "nodo" ? termino : ""],
    queryFn: () => getOpcionesNodosCauchy(tipo, sistema, tipo === "nodo" ? termino : ""),
    enabled: tipo === "zona" || termino.length >= 2,
    staleTime: 10 * 60 * 1000,
  });

  const elegir = (c: string) => {
    setClave(c);
    if (!etiquetaTocada) setEtiqueta(titulo(c));
  };

  const reiniciar = () => {
    setClave(""); setBusqueda(""); setTermino(""); setEtiqueta(""); setEtiquetaTocada(false);
  };

  const agregar = useMutation({
    mutationFn: () => addNodoCauchy({ etiqueta: etiqueta.trim(), tipo, sistema, clave }),
    onSuccess: () => { reiniciar(); onAgregado(); },
  });

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
      <div className="text-sm font-medium text-slate-800">Agregar nodo o zona</div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-500 space-y-1">
          <div>Tipo</div>
          <select
            value={tipo}
            onChange={(e) => { setTipo(e.target.value as TipoPuntoCauchy); reiniciar(); }}
            className="block rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-800"
          >
            <option value="zona">Zona de carga (PND)</option>
            <option value="nodo">Nodo (PML)</option>
          </select>
        </label>
        <label className="text-xs text-slate-500 space-y-1">
          <div>Sistema</div>
          <select
            value={sistema}
            onChange={(e) => { setSistema(e.target.value); reiniciar(); }}
            className="block rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-800"
          >
            <option value="SIN">SIN</option>
            <option value="BCA">BCA</option>
            <option value="BCS">BCS</option>
          </select>
        </label>

        {tipo === "zona" ? (
          <label className="text-xs text-slate-500 space-y-1">
            <div>Zona de carga</div>
            <select
              value={clave}
              onChange={(e) => elegir(e.target.value)}
              className="block w-56 rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-800"
            >
              <option value="">{opciones.isLoading ? "Cargando…" : "Elige una zona"}</option>
              {(opciones.data ?? []).map((o) => (
                <option key={o.clave} value={o.clave}>{o.clave}</option>
              ))}
            </select>
          </label>
        ) : (
          <div className="text-xs text-slate-500 space-y-1 relative">
            <div>Nodo</div>
            <input
              value={clave || busqueda}
              onChange={(e) => { setClave(""); setBusqueda(e.target.value); }}
              placeholder="Clave o nombre, p. ej. PUE"
              className="block w-56 rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-800"
            />
            {!clave && termino.length >= 2 && (
              <ul className="absolute z-10 mt-1 w-80 max-h-64 overflow-auto rounded border border-slate-200 bg-white shadow-lg">
                {opciones.isLoading && <li className="px-3 py-2 text-slate-400">Buscando…</li>}
                {opciones.data?.length === 0 && (
                  <li className="px-3 py-2 text-slate-400">Sin coincidencias en {sistema}.</li>
                )}
                {(opciones.data ?? []).map((o) => (
                  <li key={o.clave}>
                    <button
                      type="button"
                      onClick={() => elegir(o.clave)}
                      className="w-full text-left px-3 py-1.5 hover:bg-slate-50"
                    >
                      <span className="font-mono text-slate-800">{o.clave}</span>
                      <span className="text-slate-500"> · {o.nombre}</span>
                      {o.zona_carga && <div className="text-slate-400">{o.zona_carga}</div>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <label className="text-xs text-slate-500 space-y-1">
          <div>Etiqueta</div>
          <input
            value={etiqueta}
            maxLength={60}
            onChange={(e) => { setEtiqueta(e.target.value); setEtiquetaTocada(true); }}
            placeholder="Nombre en reportes"
            className="block w-44 rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-800"
          />
        </label>

        <button
          type="button"
          onClick={() => agregar.mutate()}
          disabled={!clave || !etiqueta.trim() || agregar.isPending}
          className="inline-flex items-center gap-1.5 rounded bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-800 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          {agregar.isPending ? "Agregando…" : "Agregar"}
        </button>
      </div>
      {agregar.isError && (
        <div className="text-sm text-red-600">{(agregar.error as Error).message}</div>
      )}
    </section>
  );
}

/** "SAN LUIS POTOSI" -> "San Luis Potosi"; las claves de nodo se dejan igual. */
function titulo(clave: string): string {
  if (/\d/.test(clave)) return clave;
  return clave.toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());
}

function num(v: number | null): string {
  return v == null ? "—" : v.toLocaleString("es-MX", { maximumFractionDigits: 1, minimumFractionDigits: 1 });
}

function fecha(iso: string | null): string {
  if (!iso) return "";
  const [a, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${a}`;
}
